import { useCallback, useEffect, useState } from "react";
import { ChefHat, Plus, Save, Trash2, X } from "lucide-react";
import { platformClient, formatPlatformError } from "./platformApi";

const inputCls =
    "mt-2 w-full border-b border-white/20 bg-transparent pb-2.5 text-sm text-bone placeholder:text-ash/50 transition-colors focus:border-gold focus:outline-none";
const labelCls = "text-[10px] uppercase tracking-[0.25em] text-ash";
const euro = (v) => `€ ${Number(v || 0).toLocaleString("nl-NL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const MATERIAL_CATEGORIES = [
    { id: "grondstof", label: "Grondstof" },
    { id: "hulpstof", label: "Hulpstof" },
    { id: "verpakking", label: "Verpakking" },
    { id: "overig", label: "Overig" },
];

const EMPTY_MATERIAL = { name: "", quantity: 1, unit: "stuk", unit_cost: "", category: "grondstof" };
const EMPTY_RECIPE = { name: "", description: "", materials: [{ ...EMPTY_MATERIAL }], labor_hours: "", machine: "", machine_hours: "", energy_kwh: "", batch_yield: 1, margin_perc: 30 };

export const RecipesTab = ({ machines }) => {
    const [recipes, setRecipes] = useState([]);
    const [form, setForm] = useState(EMPTY_RECIPE);
    const [showForm, setShowForm] = useState(false);
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    const [reqState, setReqState] = useState({});

    const calcRequirements = async (r) => {
        const batches = Number(reqState[r.id]?.batches) || 1;
        try {
            const { data } = await platformClient.get(`/recipes/${r.id}/requirements`, { params: { batches } });
            setReqState((s) => ({ ...s, [r.id]: { ...s[r.id], result: data } }));
        } catch {}
    };

    const load = useCallback(async () => {
        try {
            const { data } = await platformClient.get("/recipes");
            setRecipes(data);
        } catch {}
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    const create = async (e) => {
        e.preventDefault();
        setBusy(true);
        setError("");
        try {
            await platformClient.post("/recipes", {
                ...form,
                labor_hours: Number(form.labor_hours) || 0,
                machine_hours: Number(form.machine_hours) || 0,
                energy_kwh: Number(form.energy_kwh) || 0,
                batch_yield: Number(form.batch_yield) || 1,
                margin_perc: Number(form.margin_perc) || 0,
                materials: form.materials
                    .filter((m) => m.name.trim())
                    .map((m) => ({ ...m, quantity: Number(m.quantity) || 1, unit_cost: Number(m.unit_cost) || 0 })),
            });
            setForm(EMPTY_RECIPE);
            setShowForm(false);
            load();
        } catch (err) {
            setError(formatPlatformError(err.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    const remove = async (r) => {
        if (!window.confirm(`Receptuur ${r.name} verwijderen?`)) return;
        try {
            await platformClient.delete(`/recipes/${r.id}`);
            load();
        } catch {}
    };

    const setMat = (idx, key, val) =>
        setForm({ ...form, materials: form.materials.map((m, i) => (i === idx ? { ...m, [key]: val } : m)) });

    return (
        <div data-testid="recipes-tab" className="mt-8">
            <div className="flex items-center justify-between">
                <p className="text-sm text-ash">
                    Kostprijs = materiaal + arbeid + machine + overhead. Adviesprijs volgt uit je marge.
                </p>
                <button data-testid="recipe-add-button" onClick={() => setShowForm((v) => !v)}
                    className="flex items-center gap-2 border border-gold/50 px-5 py-3 text-xs uppercase tracking-[0.2em] text-gold transition-colors hover:bg-gold hover:text-black">
                    {showForm ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                    {showForm ? "Sluiten" : "Nieuwe receptuur"}
                </button>
            </div>

            {showForm ? (
                <form data-testid="recipe-form" onSubmit={create} className="mt-6 space-y-6 border border-white/10 bg-coal p-8">
                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                        <div>
                            <label className={labelCls}>Naam *</label>
                            <input data-testid="recipe-form-name" required className={inputCls} value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Bijv. Trappelleuning RVS (per meter)" />
                        </div>
                        <div>
                            <label className={labelCls}>Omschrijving</label>
                            <input data-testid="recipe-form-desc" className={inputCls} value={form.description}
                                onChange={(e) => setForm({ ...form, description: e.target.value })} />
                        </div>
                    </div>
                    <div>
                        <p className={labelCls}>Materialen</p>
                        <div className="mt-3 space-y-3">
                            {form.materials.map((m, idx) => (
                                <div key={idx} className="flex flex-wrap items-end gap-3">
                                    <input data-testid={`recipe-mat-name-${idx}`} className={`${inputCls} min-w-[160px] flex-1`} value={m.name}
                                        onChange={(e) => setMat(idx, "name", e.target.value)} placeholder="Materiaal" />
                                    <select data-testid={`recipe-mat-cat-${idx}`} className={`${inputCls} w-32 bg-ink`} value={m.category}
                                        onChange={(e) => setMat(idx, "category", e.target.value)}>
                                        {MATERIAL_CATEGORIES.map((c) => (
                                            <option key={c.id} value={c.id}>{c.label}</option>
                                        ))}
                                    </select>
                                    <input data-testid={`recipe-mat-qty-${idx}`} type="number" min="0" step="any" className={`${inputCls} w-20`} value={m.quantity}
                                        onChange={(e) => setMat(idx, "quantity", e.target.value)} placeholder="Aant." />
                                    <input data-testid={`recipe-mat-unit-${idx}`} className={`${inputCls} w-24`} value={m.unit}
                                        onChange={(e) => setMat(idx, "unit", e.target.value)} placeholder="Eenheid" />
                                    <input data-testid={`recipe-mat-cost-${idx}`} type="number" min="0" step="any" className={`${inputCls} w-28`} value={m.unit_cost}
                                        onChange={(e) => setMat(idx, "unit_cost", e.target.value)} placeholder="€ p/eenh" />
                                    {form.materials.length > 1 ? (
                                        <button type="button" onClick={() => setForm({ ...form, materials: form.materials.filter((_, i) => i !== idx) })}
                                            className="pb-2.5 text-ash transition-colors hover:text-red-300">
                                            <X className="h-4 w-4" />
                                        </button>
                                    ) : null}
                                </div>
                            ))}
                        </div>
                        <button type="button" data-testid="recipe-mat-add"
                            onClick={() => setForm({ ...form, materials: [...form.materials, { ...EMPTY_MATERIAL }] })}
                            className="mt-4 flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-gold transition-colors hover:text-bone">
                            <Plus className="h-3.5 w-3.5" /> Materiaal toevoegen
                        </button>
                    </div>
                    <div className="grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-6">
                        <div>
                            <label className={labelCls}>Arbeid (uren)</label>
                            <input data-testid="recipe-form-labor" type="number" min="0" step="any" className={inputCls} value={form.labor_hours}
                                onChange={(e) => setForm({ ...form, labor_hours: e.target.value })} />
                        </div>
                        <div>
                            <label className={labelCls}>Machine</label>
                            <select data-testid="recipe-form-machine" className={`${inputCls} bg-ink`} value={form.machine}
                                onChange={(e) => setForm({ ...form, machine: e.target.value })}>
                                <option value="">—</option>
                                {machines.map((m) => (
                                    <option key={m.id} value={m.name}>{m.name}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className={labelCls}>Machine (uren)</label>
                            <input data-testid="recipe-form-machine-hours" type="number" min="0" step="any" className={inputCls} value={form.machine_hours}
                                onChange={(e) => setForm({ ...form, machine_hours: e.target.value })} />
                        </div>
                        <div>
                            <label className={labelCls}>Energie (kWh)</label>
                            <input data-testid="recipe-form-energy" type="number" min="0" step="any" className={inputCls} value={form.energy_kwh}
                                onChange={(e) => setForm({ ...form, energy_kwh: e.target.value })} />
                        </div>
                        <div>
                            <label className={labelCls}>Batch-opbrengst</label>
                            <input data-testid="recipe-form-yield" type="number" min="1" step="any" className={inputCls} value={form.batch_yield}
                                onChange={(e) => setForm({ ...form, batch_yield: e.target.value })} />
                        </div>
                        <div>
                            <label className={labelCls}>Marge %</label>
                            <input data-testid="recipe-form-margin" type="number" min="0" step="any" className={inputCls} value={form.margin_perc}
                                onChange={(e) => setForm({ ...form, margin_perc: e.target.value })} />
                        </div>
                    </div>
                    {error ? <p data-testid="recipe-form-error" className="text-xs text-red-400">{error}</p> : null}
                    <button data-testid="recipe-form-submit" disabled={busy}
                        className="border border-gold bg-gold px-8 py-3 text-xs uppercase tracking-[0.25em] text-black transition-colors hover:bg-transparent hover:text-gold disabled:opacity-50">
                        {busy ? "Bezig…" : "Receptuur opslaan"}
                    </button>
                </form>
            ) : null}

            <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-2">
                {recipes.length === 0 ? (
                    <p data-testid="recipes-empty" className="border border-white/10 bg-coal p-10 text-sm text-ash md:col-span-2">
                        Nog geen recepturen. Maak je eerste kostprijscalculatie.
                    </p>
                ) : (
                    recipes.map((r) => (
                        <div key={r.id} data-testid={`recipe-card-${r.id}`} className="border border-white/10 bg-coal p-6">
                            <div className="flex items-start justify-between gap-3">
                                <div>
                                    <p className="flex items-center gap-2 font-display text-lg tracking-tight">
                                        <ChefHat className="h-4 w-4 text-gold" /> {r.name}
                                    </p>
                                    {r.description ? <p className="mt-1 text-xs text-ash">{r.description}</p> : null}
                                </div>
                                <button data-testid={`recipe-delete-${r.id}`} onClick={() => remove(r)}
                                    className="text-ash/50 transition-colors hover:text-red-300" aria-label="Verwijderen">
                                    <Trash2 className="h-4 w-4" />
                                </button>
                            </div>
                            <div className="mt-4 space-y-1.5 text-sm">
                                {Object.entries(r.calc.by_category || {}).map(([cat, val]) => (
                                    <p key={cat} className="flex justify-between text-ash"><span className="capitalize">{cat}</span><span>{euro(val)}</span></p>
                                ))}
                                <p className="flex justify-between text-ash"><span>Arbeid ({r.labor_hours} u)</span><span>{euro(r.calc.labor_cost)}</span></p>
                                {r.machine ? <p className="flex justify-between text-ash"><span>{r.machine} ({r.machine_hours} u)</span><span>{euro(r.calc.machine_cost)}</span></p> : null}
                                {r.calc.energy_cost ? <p className="flex justify-between text-ash"><span>Energie ({r.energy_kwh} kWh)</span><span>{euro(r.calc.energy_cost)}</span></p> : null}
                                <p className="flex justify-between text-ash"><span>Overhead</span><span>{euro(r.calc.overhead_cost)}</span></p>
                            </div>
                            <div className="mt-4 border-t border-white/10 pt-3">
                                <p className="flex justify-between text-sm"><span className="uppercase tracking-[0.15em] text-ash text-[10px]">Kostprijs{(r.calc.batch_yield || 1) > 1 ? ` (batch van ${r.calc.batch_yield})` : ""}</span>
                                    <span data-testid={`recipe-cost-${r.id}`} className="text-bone">{euro(r.calc.total_cost)}</span></p>
                                <p className="mt-1 flex justify-between"><span className="uppercase tracking-[0.15em] text-gold text-[10px]">Adviesprijs ({r.calc.margin_perc}% marge)</span>
                                    <span data-testid={`recipe-price-${r.id}`} className="font-display text-xl text-gold">{euro(r.calc.suggested_price)}</span></p>
                                {(r.calc.batch_yield || 1) > 1 ? (
                                    <p data-testid={`recipe-per-unit-${r.id}`} className="mt-2 flex justify-between border-t border-white/5 pt-2 text-xs">
                                        <span className="uppercase tracking-[0.15em] text-ash">Per eenheid</span>
                                        <span className="text-bone">{euro(r.calc.cost_per_unit)} kost · <span className="text-gold">{euro(r.calc.price_per_unit)} verkoop</span></span>
                                    </p>
                                ) : null}
                            </div>
                            <div className="mt-4 border-t border-white/10 pt-4">
                                <p className={labelCls}>Grondstofbehoefte</p>
                                <div className="mt-3 flex items-end gap-2">
                                    <input data-testid={`recipe-req-batches-${r.id}`} type="number" min="1" step="any"
                                        className={`${inputCls} w-24`} placeholder="Batches"
                                        value={reqState[r.id]?.batches || ""}
                                        onChange={(e) => setReqState((s) => ({ ...s, [r.id]: { ...s[r.id], batches: e.target.value, result: undefined } }))} />
                                    <button data-testid={`recipe-req-calc-${r.id}`} onClick={() => calcRequirements(r)}
                                        className="border border-gold/50 px-4 py-2 text-[10px] uppercase tracking-[0.15em] text-gold transition-colors hover:bg-gold hover:text-black">
                                        Bereken
                                    </button>
                                </div>
                                {reqState[r.id]?.result ? (
                                    <div data-testid={`recipe-req-result-${r.id}`} className="mt-3 space-y-1.5 text-xs">
                                        <p className="text-ash">Verwachte output: <span className="text-bone">{reqState[r.id].result.expected_output}</span> eenheden</p>
                                        {reqState[r.id].result.materials.map((m, i) => (
                                            <p key={i} className="flex justify-between gap-2">
                                                <span className="text-ash">{m.name}</span>
                                                <span className={m.shortfall > 0 ? "text-red-300" : "text-bone"}>
                                                    {m.needed} {m.unit}
                                                    {m.shortfall > 0 ? <span data-testid={`recipe-req-short-${r.id}-${i}`}> · tekort {m.shortfall}</span> : " · op voorraad"}
                                                </span>
                                            </p>
                                        ))}
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export const CostsTab = () => {
    const [settings, setSettings] = useState(null);
    const [saved, setSaved] = useState(false);
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);
    const [machineRows, setMachineRows] = useState([]);

    useEffect(() => {
        (async () => {
            try {
                const [{ data: s }, { data: m }] = await Promise.all([
                    platformClient.get("/settings/costs"),
                    platformClient.get("/production/machines"),
                ]);
                setSettings(s);
                const names = [...new Set([...m.map((x) => x.name), ...Object.keys(s.machine_rates || {})])];
                setMachineRows(names.map((n) => ({ name: n, rate: (s.machine_rates || {})[n] ?? "" })));
            } catch {}
        })();
    }, []);

    const save = async (e) => {
        e.preventDefault();
        setBusy(true);
        setError("");
        setSaved(false);
        try {
            const machine_rates = {};
            for (const row of machineRows) {
                if (row.name.trim() && Number(row.rate) > 0) machine_rates[row.name.trim()] = Number(row.rate);
            }
            const { data } = await platformClient.put("/settings/costs", {
                labor_rate: Number(settings.labor_rate) || 0,
                machine_rates,
                fixed_personnel: Number(settings.fixed_personnel) || 0,
                fixed_housing: Number(settings.fixed_housing) || 0,
                fixed_other: Number(settings.fixed_other) || 0,
                custom_costs: (settings.custom_costs || [])
                    .filter((c) => c.name.trim())
                    .map((c) => ({ name: c.name.trim(), monthly: Number(c.monthly) || 0 })),
                energy_rate: Number(settings.energy_rate) || 0,
                productive_hours: Number(settings.productive_hours) || 0,
            });
            setSettings((s) => ({ ...s, ...data }));
            setSaved(true);
            setTimeout(() => setSaved(false), 4000);
        } catch (err) {
            setError(formatPlatformError(err.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    if (!settings) return <p className="mt-8 text-xs uppercase tracking-[0.3em] text-ash">Laden…</p>;

    const fixedTotal = (Number(settings.fixed_personnel) || 0) + (Number(settings.fixed_housing) || 0) + (Number(settings.fixed_other) || 0)
        + (settings.custom_costs || []).reduce((s, c) => s + (Number(c.monthly) || 0), 0);

    return (
        <form data-testid="costs-form" onSubmit={save} className="mt-8 space-y-8">
            <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
                <div className="border border-white/10 bg-coal p-6">
                    <p className={labelCls}>Tarieven</p>
                    <div className="mt-4">
                        <label className={labelCls}>Uurtarief arbeid (€/uur)</label>
                        <input data-testid="costs-labor-rate" type="number" min="0" step="any" className={inputCls} value={settings.labor_rate}
                            onChange={(e) => setSettings({ ...settings, labor_rate: e.target.value })} />
                    </div>
                    <div className="mt-5">
                        <label className={labelCls}>Energie (€/kWh)</label>
                        <input data-testid="costs-energy-rate" type="number" min="0" step="any" className={inputCls} value={settings.energy_rate ?? ""}
                            onChange={(e) => setSettings({ ...settings, energy_rate: e.target.value })} placeholder="Bijv. 0,28" />
                    </div>
                    <p className={`${labelCls} mt-6`}>Machinetarieven (€/uur)</p>
                    <div className="mt-3 space-y-3">
                        {machineRows.map((row, idx) => (
                            <div key={idx} className="flex items-end gap-3">
                                <input data-testid={`costs-machine-name-${idx}`} className={`${inputCls} flex-1`} value={row.name}
                                    onChange={(e) => setMachineRows(machineRows.map((r, i) => (i === idx ? { ...r, name: e.target.value } : r)))} placeholder="Machine" />
                                <input data-testid={`costs-machine-rate-${idx}`} type="number" min="0" step="any" className={`${inputCls} w-28`} value={row.rate}
                                    onChange={(e) => setMachineRows(machineRows.map((r, i) => (i === idx ? { ...r, rate: e.target.value } : r)))} placeholder="€/uur" />
                            </div>
                        ))}
                    </div>
                    <button type="button" data-testid="costs-machine-add"
                        onClick={() => setMachineRows([...machineRows, { name: "", rate: "" }])}
                        className="mt-4 flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-gold transition-colors hover:text-bone">
                        <Plus className="h-3.5 w-3.5" /> Machine toevoegen
                    </button>
                </div>
                <div className="border border-white/10 bg-coal p-6">
                    <p className={labelCls}>Vaste lasten per maand</p>
                    <div className="mt-4 space-y-5">
                        <div>
                            <label className={labelCls}>Personeel (€)</label>
                            <input data-testid="costs-fixed-personnel" type="number" min="0" step="any" className={inputCls} value={settings.fixed_personnel}
                                onChange={(e) => setSettings({ ...settings, fixed_personnel: e.target.value })} />
                        </div>
                        <div>
                            <label className={labelCls}>Huisvesting (€)</label>
                            <input data-testid="costs-fixed-housing" type="number" min="0" step="any" className={inputCls} value={settings.fixed_housing}
                                onChange={(e) => setSettings({ ...settings, fixed_housing: e.target.value })} />
                        </div>
                        <div>
                            <label className={labelCls}>Overig (€)</label>
                            <input data-testid="costs-fixed-other" type="number" min="0" step="any" className={inputCls} value={settings.fixed_other}
                                onChange={(e) => setSettings({ ...settings, fixed_other: e.target.value })} />
                        </div>
                        <div className="border-t border-white/10 pt-5">
                            <p className={labelCls}>Eigen kostenposten (€/maand)</p>
                            <div className="mt-3 space-y-3">
                                {(settings.custom_costs || []).map((cc, idx) => (
                                    <div key={idx} className="flex items-end gap-3">
                                        <input data-testid={`costs-custom-name-${idx}`} className={`${inputCls} flex-1`} value={cc.name}
                                            onChange={(e) => setSettings({ ...settings, custom_costs: settings.custom_costs.map((c, i) => (i === idx ? { ...c, name: e.target.value } : c)) })}
                                            placeholder="Bijv. Energie, Verpakking, Transport" />
                                        <input data-testid={`costs-custom-amount-${idx}`} type="number" min="0" step="any" className={`${inputCls} w-28`} value={cc.monthly}
                                            onChange={(e) => setSettings({ ...settings, custom_costs: settings.custom_costs.map((c, i) => (i === idx ? { ...c, monthly: e.target.value } : c)) })}
                                            placeholder="€/mnd" />
                                        <button type="button" data-testid={`costs-custom-del-${idx}`}
                                            onClick={() => setSettings({ ...settings, custom_costs: settings.custom_costs.filter((_, i) => i !== idx) })}
                                            className="pb-2.5 text-ash transition-colors hover:text-red-300">
                                            <X className="h-4 w-4" />
                                        </button>
                                    </div>
                                ))}
                            </div>
                            <button type="button" data-testid="costs-custom-add"
                                onClick={() => setSettings({ ...settings, custom_costs: [...(settings.custom_costs || []), { name: "", monthly: "" }] })}
                                className="mt-3 flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-gold transition-colors hover:text-bone">
                                <Plus className="h-3.5 w-3.5" /> Kostenpost toevoegen
                            </button>
                        </div>
                        <div>
                            <label className={labelCls}>Productieve uren per maand</label>
                            <input data-testid="costs-productive-hours" type="number" min="0" step="any" className={inputCls} value={settings.productive_hours}
                                onChange={(e) => setSettings({ ...settings, productive_hours: e.target.value })} />
                        </div>
                    </div>
                </div>
            </div>
            <div data-testid="costs-summary" className="border border-gold/30 bg-gold/5 p-6">
                <p className="text-[10px] uppercase tracking-[0.25em] text-gold">Berekende overhead</p>
                <p className="mt-3 font-display text-3xl tracking-tight">
                    {euro(settings.overhead_rate)} <span className="text-base text-ash">per productie-uur</span>
                </p>
                <p className="mt-2 text-xs text-ash">
                    {euro(fixedTotal)} vaste lasten ÷ {settings.productive_hours || 0} productieve uren. Dit tarief rekent automatisch mee in je recepturen.
                </p>
            </div>
            {error ? <p data-testid="costs-error" className="text-xs text-red-400">{error}</p> : null}
            {saved ? <p data-testid="costs-saved" className="text-xs uppercase tracking-[0.2em] text-gold">Opgeslagen</p> : null}
            <button data-testid="costs-save" disabled={busy}
                className="flex items-center gap-2 border border-gold bg-gold px-8 py-3 text-xs uppercase tracking-[0.25em] text-black transition-colors hover:bg-transparent hover:text-gold disabled:opacity-50">
                <Save className="h-4 w-4" /> {busy ? "Bezig…" : "Instellingen opslaan"}
            </button>
        </form>
    );
};
